/**
 * JWT token validation utilities
 */

export interface JWTPayload {
  exp?: number;
  iat?: number;
  sub?: string;
  [key: string]: any;
}

/**
 * Validates JWT token format (header.payload.signature)
 */
export const isValidJWTFormat = (token: string): boolean => {
  if (!token || typeof token !== 'string') {
    return false;
  }

  // JWT should have exactly 3 parts separated by dots
  const parts = token.split('.');
  if (parts.length !== 3) {
    return false;
  }

  // Each part should be base64url encoded (non-empty)
  return parts.every(part => part.length > 0);
};

/**
 * Decodes JWT payload without verification (for client-side validation only)
 * WARNING: This does not verify the signature - only use for expiration checks
 */
export const decodeJWTPayload = (token: string): JWTPayload | null => {
  try {
    if (!isValidJWTFormat(token)) {
      return null;
    }

    const parts = token.split('.');
    const payload = parts[1];
    
    // Decode base64url
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Failed to decode JWT payload:', error);
    return null;
  }
};

/**
 * Checks if JWT token is expired (client-side check only)
 */
export const isTokenExpired = (token: string): boolean => {
  const payload = decodeJWTPayload(token);
  if (!payload || !payload.exp) {
    return true; // Treat invalid tokens as expired
  }

  // JWT exp is in seconds, Date.now() is in milliseconds
  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp < currentTime;
};

/**
 * Gets token expiration date
 */
export const getTokenExpiration = (token: string): Date | null => {
  const payload = decodeJWTPayload(token);
  if (!payload || !payload.exp) {
    return null;
  }

  // JWT exp is in seconds, convert to milliseconds
  return new Date(payload.exp * 1000);
};

/**
 * Checks if token will expire within the specified minutes
 */
export const willTokenExpireSoon = (token: string, minutesBeforeExpiry: number = 5): boolean => {
  const payload = decodeJWTPayload(token);
  if (!payload || !payload.exp) {
    return true; // Treat invalid tokens as expiring soon
  }

  const currentTime = Math.floor(Date.now() / 1000);
  const expirationTime = payload.exp;
  const timeUntilExpiry = expirationTime - currentTime;
  
  // Convert minutes to seconds
  const thresholdSeconds = minutesBeforeExpiry * 60;
  
  return timeUntilExpiry <= thresholdSeconds;
};

/**
 * Validates token and returns validation result
 */
export const validateToken = (token: string): {
  isValid: boolean;
  isExpired: boolean;
  expiresAt: Date | null;
  reason?: string;
} => {
  if (!token) {
    return {
      isValid: false,
      isExpired: true,
      expiresAt: null,
      reason: 'Token is empty'
    };
  }

  if (!isValidJWTFormat(token)) {
    return {
      isValid: false,
      isExpired: true,
      expiresAt: null,
      reason: 'Invalid JWT format'
    };
  }

  const expired = isTokenExpired(token);
  const expiresAt = getTokenExpiration(token);

  return {
    isValid: !expired,
    isExpired: expired,
    expiresAt,
    reason: expired ? 'Token is expired' : undefined
  };
};

/**
 * Sanitizes token for logging (shows only first/last few characters)
 */
export const sanitizeTokenForLogging = (token: string): string => {
  if (!token || token.length < 10) {
    return '[INVALID_TOKEN]';
  }
  
  const start = token.substring(0, 8);
  const end = token.substring(token.length - 8);
  return `${start}...${end}`;
};
